/**
 * Comisiones comerciales.
 *
 * Se comisiona sobre lo **cobrado**, con porcentaje **por comercial**, y
 * mientras el cliente siga en su cartera. Esta pantalla es el cálculo del
 * período; el informe imprimible es lo que se lleva administración para la
 * nómina.
 *
 * La sección «Cobros que no comisionan» no es un detalle: es lo que explica
 * por qué la suma de comisiones no cuadra con la caja del mes. Sin ella, la
 * primera vez que alguien compare las dos cifras pensará que el sistema
 * miente.
 */

import { useCallback, useEffect, useState } from 'react';
import { hasPermission, useSession } from '../session/session-context.js';
import { ApiError } from '../session/api-error.js';

interface Line {
  salesRepUserId: string;
  salesRepName: string;
  rate: string | null;
  customerCount: string;
  collectedTotal: string;
  collectedBase: string;
  amount: string | null;
  settled: boolean;
}

interface Report {
  from: string;
  to: string;
  lines: Line[];
  excluded: {
    customerWithoutRep: string;
    unallocated: string;
    repWithoutRate: string;
  };
}

interface Settlement {
  id: string;
  sales_rep_name: string;
  period_start: string;
  period_end: string;
  collected_base: string;
  rate: string;
  amount: string;
  status: string;
}

/** Importes en euros, sin pasar por coma flotante en ningún punto. */
function eur(value: string | null): string {
  if (value === null) return '—';
  const [whole = '0', decimals = '00'] = value.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${grouped},${decimals.padEnd(2, '0').slice(0, 2)} €`;
}

function pct(rate: string | null): string {
  if (rate === null) return '—';
  return `${(Number(rate) * 100).toFixed(2).replace(/\.?0+$/, '').replace('.', ',')} %`;
}

/** Primer y último día del mes en curso. */
function currentMonth(): { from: string; to: string } {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  return {
    from: new Date(Date.UTC(y, m, 1)).toISOString().slice(0, 10),
    to: new Date(Date.UTC(y, m + 1, 0)).toISOString().slice(0, 10),
  };
}

export function CommissionsScreen(): React.ReactNode {
  const { api, user } = useSession();
  const month = currentMonth();

  const [from, setFrom] = useState(month.from);
  const [to, setTo] = useState(month.to);
  const [report, setReport] = useState<Report | null>(null);
  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const canSettle = hasPermission(user, 'treasury.update');

  const load = useCallback(async () => {
    setError(null);
    try {
      const [data, closed] = await Promise.all([
        api.get<Report>(`/commissions?from=${from}&to=${to}`),
        api.get<{ items: Settlement[] }>('/commissions/settlements').catch(() => ({ items: [] })),
      ]);
      setReport(data);
      setSettlements(closed.items);
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'No se han podido calcular las comisiones.',
      );
    }
  }, [api, from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  async function settle(line: Line): Promise<void> {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      const result = await api.post<{ amount: string }>('/commissions/settle', {
        salesRepUserId: line.salesRepUserId,
        from,
        to,
      });
      setNotice(`Liquidado ${eur(result.amount)} a ${line.salesRepName}.`);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido liquidar.');
    } finally {
      setBusy(false);
    }
  }

  const excluded = report?.excluded;
  const hayExcluido =
    excluded !== undefined &&
    (excluded.customerWithoutRep !== '0' ||
      excluded.unallocated !== '0' ||
      excluded.repWithoutRate !== '0');

  return (
    <section className="screen">
      <header className="screen__head">
        <h1>Comisiones</h1>
        <p className="screen__note">
          Sobre la base imponible cobrada en el período. El IVA no comisiona, y una factura
          emitida y no cobrada tampoco.
        </p>
      </header>

      {error ? <p className="screen__error" role="alert">{error}</p> : null}
      {notice ? <p className="screen__note screen__note--strong" role="status">{notice}</p> : null}

      <div className="card radar__form">
        <div className="radar__fields">
          <label>
            Desde
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label>
            Hasta
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
        </div>
        <a
          className="button"
          href={`/v1/commissions/report?from=${from}&to=${to}`}
          target="_blank"
          rel="noreferrer"
        >
          Informe para administración
        </a>
      </div>

      <div className="card radar">
        <h2>Período {from} — {to}</h2>
        <table className="table__grid">
          <thead>
            <tr>
              <th>Comercial</th>
              <th className="table__num">Clientes</th>
              <th className="table__num">Cobrado</th>
              <th className="table__num">Base imponible</th>
              <th className="table__num">%</th>
              <th className="table__num">A percibir</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {(report?.lines ?? []).map((l) => (
              <tr key={l.salesRepUserId}>
                <td>{l.salesRepName}</td>
                <td className="table__num">{l.customerCount}</td>
                <td className="table__num">{eur(l.collectedTotal)}</td>
                <td className="table__num">{eur(l.collectedBase)}</td>
                <td className="table__num">
                  {l.rate === null ? (
                    <span className="due--late">sin asignar</span>
                  ) : (
                    pct(l.rate)
                  )}
                </td>
                <td className="table__num">{eur(l.amount)}</td>
                <td>
                  {l.settled ? (
                    <span className="pill pill--ok">Liquidada</span>
                  ) : canSettle && l.amount !== null && l.amount !== '0.00' ? (
                    <button
                      type="button"
                      className="button button--quiet"
                      disabled={busy}
                      onClick={() => void settle(l)}
                    >
                      Liquidar
                    </button>
                  ) : (
                    <span className="due--none">Provisional</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {report !== null && report.lines.length === 0 ? (
          <p className="table__empty">
            Ningún cobro en el período, o ningún cliente con comercial asignado.
          </p>
        ) : null}
      </div>

      {hayExcluido ? (
        <div className="card radar radar--late">
          <h2>Cobros que no comisionan</h2>
          <p className="field__help">
            Explican la diferencia entre la caja del período y la suma de comisiones. Las dos
            primeras se arreglan asignando el comercial del cliente o aplicando el cobro a su
            factura.
          </p>
          <table className="table__grid">
            <tbody>
              <tr>
                <td>De clientes sin comercial asignado</td>
                <td className="table__num">{eur(excluded.customerWithoutRep)}</td>
              </tr>
              <tr>
                <td>Cobrado y todavía sin aplicar a factura</td>
                <td className="table__num">{eur(excluded.unallocated)}</td>
              </tr>
              <tr>
                <td>De comerciales sin porcentaje asignado</td>
                <td className="table__num">{eur(excluded.repWithoutRate)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : null}

      {settlements.length > 0 ? (
        <div className="card radar">
          <h2>Liquidaciones cerradas</h2>
          <p className="field__help">
            El importe y el porcentaje quedaron congelados al liquidar. Si después se devuelve
            un recibo del período, la corrección va en el siguiente — como en una nómina.
          </p>
          <table className="table__grid">
            <thead>
              <tr>
                <th>Comercial</th>
                <th>Período</th>
                <th className="table__num">Base</th>
                <th className="table__num">%</th>
                <th className="table__num">Importe</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {settlements.map((s) => (
                <tr key={s.id}>
                  <td>{s.sales_rep_name}</td>
                  <td>
                    {s.period_start} — {s.period_end}
                  </td>
                  <td className="table__num">{eur(s.collected_base)}</td>
                  <td className="table__num">{pct(s.rate)}</td>
                  <td className="table__num">{eur(s.amount)}</td>
                  <td>{s.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
